<template>
  <v-row class="justify-center align-self-start">
    <v-col class="col-4 mt-3">
      <v-card class="pa-3">
        <v-card-title class="heading-1 darkestblue--text">
          Custom Analytics
        </v-card-title>

        <v-card-text
          v-if="!customAnalyticOptions || !customAnalyticOptions.length"
        >
          <v-row>
            <p class="px-4 pt-3">
              No reports found.
            </p>

            <p class="px-4 pb-3">
              <a
                href="mailto:iadops@oao.freshdesk.com"
                target="_blank"
              >Contact us</a>
              if this is an unexpected response or would like to learn more about Custom Analytics.
            </p>
          </v-row>
        </v-card-text>

        <v-card-text v-else>
          <v-row
            v-for="(customAnalytics, billingCode) in currentCustomAnalyticOptionsByOrg"
            :key="billingCode"
            class="pt-2"
          >
            <v-col>
              <v-row>
                <v-col class="pb-0">
                  <h4
                    v-if="isInternalUser || currentCustomAnalyticOptionsByOrg.length > 1"
                    class="darkestblue--text"
                  >
                    {{ billingCode || 'Unknown Billing Code' }}
                  </h4>
                </v-col>
              </v-row>

              <v-row>
                <v-col class="pt-0">
                  <v-list
                    dense
                    class="py-0"
                  >
                    <v-list-item
                      v-for="(customAnalytic, crIndex) in customAnalytics"
                      :key="crIndex"
                      :class="isInternalUser ? 'border-left mt-3' : ''"
                    >
                      <v-list-item-content class="pt-0">
                        <v-list-item-title>
                          <v-row class="mt-0">
                            <v-col class="subtitle-2 font-weight-bold pt-0">
                              <router-link
                                :to="customAnalytic.href"
                              >
                                {{ customAnalytic.name || 'Report Name Not Set' }}
                              </router-link>
                            </v-col>
                          </v-row>

                          <v-row
                            v-if="isInternalUser && isCustomAnalyticUser"
                          >
                            <v-col class="pt-0">
                              <v-row
                                class="cursor-pointer"
                                @click="updateShowCustomAnalyticUsersByBillingCode(billingCode)"
                              >
                                <v-col :class="customAnalyticUsersByBillingCode[billingCode].length ? 'py-0' : 'pt-0'">
                                  <span class="gray--text">
                                    {{ customAnalyticUsersByBillingCode[billingCode].length }}
                                    {{ customAnalyticUsersByBillingCode[billingCode].length === 1 ? 'User' : 'Users' }}
                                  </span>
                                </v-col>
                              </v-row>

                              <v-row v-if="showCustomAnalyticUsersByBillingCode.includes(billingCode)">
                                <v-col class="pa-0">
                                  <v-list
                                    dense
                                    class="pt-0"
                                  >
                                    <v-list-item
                                      v-for="(user, userIndex) in customAnalyticUsersByBillingCode[billingCode]"
                                      :key="userIndex"
                                      class="px-3"
                                    >
                                      <v-list-item-content class="py-0">
                                        <v-list-item-title>
                                          <span class="midgray-text">
                                            {{ user.docId }}
                                          </span>
                                        </v-list-item-title>
                                      </v-list-item-content>
                                    </v-list-item>
                                  </v-list>
                                </v-col>
                              </v-row>
                            </v-col>
                          </v-row>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'CustomAnalyticsList',
  data() {
    return {
      showCustomAnalyticUsersByBillingCode: [],
    }
  },
  computed: {
    ...mapAuthGetters(['isCustomAnalyticUser', 'isInternalUser']),
    ...mapToolsState({
      searchResults: (state) => state.searchResults,
    }),
    customAnalyticOptions() {
      return this.searchResults.endpoints['custom-analytics']?.items?.list || []
    },
    currentCustomAnalyticOptionsByOrg() {
      const customAnalyticOptions = this.associationId
        ? this.customAnalyticOptions.filter((option) => option.docId === this.associationId)
        : this.customAnalyticOptions

      return customAnalyticOptions.reduce(() => {
        const customAnalyticOptionsByBillingCode = {}
        customAnalyticOptions.forEach((option) => {
          if (!customAnalyticOptionsByBillingCode[option.billingCode]) {
            customAnalyticOptionsByBillingCode[option.billingCode] = []
          }
          customAnalyticOptionsByBillingCode[option.billingCode].push(option)
        })
        return customAnalyticOptionsByBillingCode
      }, {})
    },
    customAnalyticUsers() {
      const customAnalyticUsers = this.searchResults.endpoints.person?.items?.list || []
      return customAnalyticUsers.filter((user) => user.hasCustomAnalytics)
    },
    customAnalyticUsersByBillingCode() {
      return this.customAnalyticOptions.reduce((customAnalyticUsers, org) => {
        customAnalyticUsers[org.billingCode] = this.getCustomAnalyticUsersByBillingCode(org.billingCode)
        return customAnalyticUsers
      }, {})
    },
  },
  methods: {
    getCustomAnalyticUsersByBillingCode(billingCode) {
      return this.customAnalyticUsers.filter((user) => {
        const isUserAssignedToCustomAnalytic = user.isPersonUserInternal || user.billingCodes?.includes(billingCode)
        return user.isPersonUserActive && isUserAssignedToCustomAnalytic
      })
    },
    updateShowCustomAnalyticUsersByBillingCode(billingCode) {
      const { showCustomAnalyticUsersByBillingCode } = this
      const foundIndex = showCustomAnalyticUsersByBillingCode.indexOf(billingCode)
      const isBillingCodeShowing = foundIndex > -1

      if (isBillingCodeShowing) {
        this.showCustomAnalyticUsersByBillingCode.splice(foundIndex, 1)
      } else {
        this.showCustomAnalyticUsersByBillingCode.push(billingCode)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.border-left {
  border-left: 3px solid $color-monochrome-offwhite;
}
</style>
